import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import '../styles/app-store-restriction.css';

import { ModalBackdrop } from './ModalBackdrop';
import { ModalContent } from './ModalContent';
import { ModalHeader } from './ModalHeader';

interface AppStoreRestrictionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal shown on iOS native app when the user tries to access features
 * restricted by Apple App Store guideline 2.5.2 (Code, Config, Live, Kubernetes, Deploy).
 *
 * Offers a single "Open in Browser" button that opens 8080.ai in Safari
 * via the native OPEN_URL bridge.
 *
 * Rendered via a React Portal to escape sidebar stacking context.
 */
export const AppStoreRestrictionModal: React.FC<AppStoreRestrictionModalProps> = ({
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleOpenBrowser = useCallback(() => {
    const rnWebView = (window as any).ReactNativeWebView;

    if (rnWebView) {
      rnWebView.postMessage(
        JSON.stringify({ type: 'OPEN_URL', url: 'https://8080.ai' }),
      );
    } else {
      window.open('https://8080.ai', '_blank');
    }
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return createPortal(
    <ModalBackdrop isVisible onClick={onClose}>
      <ModalContent onClose={onClose}>
        <ModalHeader
          title="Available on the Web"
          subtitle="This feature is available through your web browser. Tap below to continue on 8080.ai."
          onClose={onClose}
        />
        <div className="app-store-restriction-modal__body">
          <button
            className="app-store-restriction-modal__btn"
            onClick={handleOpenBrowser}
            type="button"
          >
            <i className="fa-solid fa-arrow-up-right-from-square" />
            Open in Browser
          </button>
        </div>
      </ModalContent>
    </ModalBackdrop>,
    document.body,
  );
};
